import { UserType } from "@/constants";
import {
  getGovernmentJurisdictionOptions,
  getGovernmentLicenseTypeConfigurationsOptions,
  getJurisdictionsOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  JurisdictionWithLicenseTypeConfigurationResponseSchema,
  NonRegistrantQuestionResponseSchema,
} from "@/hera-api/client/types.gen";
import { dashboardListQueryOptions } from "@/lib/dashboard-query-options";
import {
  type DashboardQuestionType,
  filterDashboardQuestions,
  shouldApplyGroupQuestionAssignmentFilter,
} from "@/lib/filter-dashboard-questions";
import { getUserType } from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export function useFilteredDashboardQuestions({
  questions,
  questionType,
  selectedJurisdictions,
  selectedLicenseTypes,
  isAuthSetupComplete,
}: {
  questions: NonRegistrantQuestionResponseSchema[] | undefined;
  questionType: DashboardQuestionType;
  selectedJurisdictions: string[];
  selectedLicenseTypes: string[];
  isAuthSetupComplete: boolean;
}) {
  const { user } = useAuth0();
  const userType = getUserType(user);
  // The government portal requests the government API audience, including
  // operators viewing it for a government user. Those tokens cannot call
  // /operator/jurisdictions.
  const onGovernmentPortal =
    typeof window !== "undefined" &&
    (window.location.pathname === "/government" ||
      window.location.pathname.startsWith("/government/"));
  const useGovernmentJurisdictionSource =
    userType === UserType.GOVERNMENT ||
    (userType === UserType.OPERATOR && onGovernmentPortal);
  const applyGroupAssignment = shouldApplyGroupQuestionAssignmentFilter(
    selectedJurisdictions,
    selectedLicenseTypes,
  );
  const loadOperatorJurisdictions =
    isAuthSetupComplete && !useGovernmentJurisdictionSource;
  const loadGovernmentJurisdictions =
    isAuthSetupComplete && useGovernmentJurisdictionSource;

  const operatorJurisdictionsQuery = useQuery({
    ...getJurisdictionsOptions(),
    enabled: loadOperatorJurisdictions,
    ...dashboardListQueryOptions,
  });
  const governmentJurisdictionQuery = useQuery({
    ...getGovernmentJurisdictionOptions(),
    enabled: loadGovernmentJurisdictions,
    ...dashboardListQueryOptions,
  });
  const governmentConfigsQuery = useQuery({
    ...getGovernmentLicenseTypeConfigurationsOptions(),
    enabled: loadGovernmentJurisdictions,
    ...dashboardListQueryOptions,
  });

  const jurisdictions = useMemo(() => {
    if (useGovernmentJurisdictionSource) {
      const jurisdiction = governmentJurisdictionQuery.data;
      if (!jurisdiction || !governmentConfigsQuery.data) {
        return [];
      }
      return [
        {
          ...jurisdiction,
          license_type_configurations: governmentConfigsQuery.data,
        } as JurisdictionWithLicenseTypeConfigurationResponseSchema,
      ];
    }
    return operatorJurisdictionsQuery.data?.jurisdictions ?? [];
  }, [
    useGovernmentJurisdictionSource,
    governmentJurisdictionQuery.data,
    governmentConfigsQuery.data,
    operatorJurisdictionsQuery.data?.jurisdictions,
  ]);

  const isJurisdictionsLoading = useGovernmentJurisdictionSource
    ? governmentJurisdictionQuery.isLoading || governmentConfigsQuery.isLoading
    : operatorJurisdictionsQuery.isLoading;
  const jurisdictionsQueryError = useGovernmentJurisdictionSource
    ? (governmentJurisdictionQuery.error ?? governmentConfigsQuery.error)
    : operatorJurisdictionsQuery.error;

  const availableQuestions = useMemo(() => {
    const list = questions ?? [];
    if (!applyGroupAssignment) {
      return filterDashboardQuestions(list, {
        questionType,
        selectedJurisdictionIds: selectedJurisdictions,
        selectedLicenseTypeIds: selectedLicenseTypes,
        jurisdictions: [],
      });
    }
    if (jurisdictions.length === 0) {
      return [];
    }
    return filterDashboardQuestions(list, {
      questionType,
      selectedJurisdictionIds: selectedJurisdictions,
      selectedLicenseTypeIds: selectedLicenseTypes,
      jurisdictions,
    });
  }, [
    questions,
    questionType,
    selectedJurisdictions,
    selectedLicenseTypes,
    applyGroupAssignment,
    jurisdictions,
  ]);

  return {
    availableQuestions,
    jurisdictions,
    isJurisdictionsLoading,
    jurisdictionsQueryError,
  };
}
